import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ShieldCheck, ShieldAlert, ShieldX, ChevronDown, ChevronUp, Mail, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityPostureCard({ user }) {
  const [data, setData] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const load = () => {
    axios.get(`${API}/dashboard/security-posture`).then(res => setData(res.data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    load();
  }, [user]);

  const openPreview = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/security-posture/nudge/preview`);
      setPreviewHtml(res.data?.html || '');
      setPreviewOpen(true);
    } catch {
      toast.error('Failed to load preview');
    }
  };

  const sendNudges = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/dashboard/security-posture/nudge`);
      toast.success(`Reminder emailed to ${res.data?.emails_sent || 0} admin(s).`);
      setPreviewOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  if (user?.role !== 'super_admin' || !data) return null;

  const { coverage_pct, status, privileged_users_total, privileged_users_with_mfa, missing_mfa_users = [] } = data;
  const Icon = status === 'green' ? ShieldCheck : (status === 'yellow' ? ShieldAlert : ShieldX);
  const styles = {
    green: { ring: 'border-emerald-200 bg-emerald-50', icon: 'text-emerald-600', bar: 'bg-emerald-500', label: 'Fully protected' },
    yellow: { ring: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', bar: 'bg-amber-500', label: 'Needs attention' },
    red: { ring: 'border-rose-200 bg-rose-50', icon: 'text-rose-600', bar: 'bg-rose-500', label: 'Not compliant' },
  }[status] || {};

  return (
    <Card className={`border ${styles.ring}`} data-testid="security-posture-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-600 font-medium flex items-center gap-2">
            <Icon className={`w-4 h-4 ${styles.icon}`} /> Security posture · Admin MFA
          </CardTitle>
          <span className={`text-[10px] uppercase tracking-widest font-semibold ${styles.icon}`}>{styles.label}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums" data-testid="mfa-coverage-pct">{coverage_pct}%</span>
            <span className="text-xs text-slate-500 tabular-nums">
              {privileged_users_with_mfa}/{privileged_users_total} admins enrolled
            </span>
          </div>
          <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full ${styles.bar} transition-all`} style={{ width: `${coverage_pct}%` }} />
          </div>
        </div>
        {missing_mfa_users.length > 0 && (
          <>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 justify-between text-xs h-8 px-2"
                onClick={() => setShowDetail(v => !v)}
                data-testid="toggle-missing-mfa"
              >
                <span>{missing_mfa_users.length} admin(s) missing MFA</span>
                {showDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs"
                onClick={openPreview}
                data-testid="preview-mfa-nudge"
                title="Preview and send the MFA reminder email"
              >
                <Mail className="w-3 h-3 mr-1" /> Send reminder
              </Button>
            </div>
            {showDetail && (
              <ul className="space-y-1.5 text-xs bg-white rounded-md border border-slate-200 p-2 max-h-40 overflow-y-auto">
                {missing_mfa_users.map(u => (
                  <li key={u.email} className="flex justify-between items-center gap-2">
                    <span className="truncate">
                      <span className="font-medium">{u.full_name || u.email}</span>
                      <span className="text-slate-400"> · {u.role}</span>
                      {u.organization_name && <span className="text-slate-400"> · {u.organization_name}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="mfa-nudge-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> Preview MFA reminder</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">This is exactly what each admin missing MFA will receive. <span className="font-medium text-slate-700">{missing_mfa_users.length}</span> recipient(s).</p>
          <div className="flex-1 overflow-y-auto border rounded-md bg-slate-50">
            <iframe title="mfa-email-preview" srcDoc={previewHtml} className="w-full min-h-[400px] bg-white" sandbox="" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Cancel</Button>
            <Button onClick={sendNudges} disabled={sending} data-testid="confirm-send-mfa-nudges">
              <Mail className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : `Send to ${missing_mfa_users.length} admin(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
